import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const VenturaCountyPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?county=Ventura&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Ventura properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Ventura County Real Estate | Buy or Sell a Home in Ventura County | Truc Doan</title>
        <meta
          name="description"
          content="Looking to buy or sell a home in Ventura County, CA? Truc Doan is your trusted Ventura County real estate agent. Explore homes for sale in Oxnard, Thousand Oaks, Simi Valley, Camarillo, Ventura, and more. Get a free home value estimate today."
        />
        <meta
          name="keywords"
          content="Ventura County real estate, homes for sale Ventura County CA, buy a home Ventura County, sell a home Ventura County, Ventura County realtor, Oxnard homes for sale, Thousand Oaks real estate, Simi Valley homes, Camarillo real estate, Ventura homes for sale, Moorpark real estate, Fillmore homes, Santa Paula real estate, Ojai homes, Port Hueneme real estate, Truc Doan Ventura County, Ventura County home value, Ventura County real estate agent"
        />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/ventura-county`} />
        <meta property="og:title" content="Ventura County Real Estate | Buy or Sell a Home in Ventura County | Truc Doan" />
        <meta property="og:description" content="Your trusted Ventura County real estate expert. Browse homes for sale in Oxnard, Thousand Oaks, Simi Valley, Camarillo, Ventura & more. Free home value estimates." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/ventura-county`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Ventura County, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/ventura-county`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a top-rated real estate agent serving Ventura County, CA. Specializing in helping buyers find their dream home and sellers maximize their home value across all Ventura County cities.",
            "areaServed": {
              "@type": "AdministrativeArea",
              "name": "Ventura County, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            padding: '10px 20px',
            background: 'rgb(14, 165, 233)',
            color: 'rgb(243, 244, 246)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            zIndex: 10
          }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>{t('ventura.h1')}</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            {t('ventura.subtitle')}
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>{t('ventura.whyTitle')}</h2>
          <p>{t('ventura.whyP1')}</p>
          <p>{t('ventura.whyP2')}</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>{t('county.buyingHome', { county: t('county.venturaCounty') })}</h2>
          <p>{t('ventura.buyingP')}</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('ventura.thousandOaks')}</h3>
              <p>{t('ventura.thousandOaksDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('ventura.simiValley')}</h3>
              <p>{t('ventura.simiValleyDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('ventura.oxnard')}</h3>
              <p>{t('ventura.oxnardDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('ventura.camarillo')}</h3>
              <p>{t('ventura.camarilloDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('ventura.venturaCity')}</h3>
              <p>{t('ventura.venturaCityDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('ventura.moorpark')}</h3>
              <p>{t('ventura.moorparkDesc')}</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>{t('county.marketSnapshot', { county: t('county.venturaCounty') })}</h2>
          <p>{t('ventura.marketP')}</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>{t('county.marketStat')}</th>
                <th>{t('county.value')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price (Ventura County)</td>
                <td>~$780,000</td>
              </tr>
              <tr>
                <td>{t('county.avgDaysOnMarket')}</td>
                <td>25–40 Days</td>
              </tr>
              <tr>
                <td>{t('county.mostActivePriceRange')}</td>
                <td>$550K – $1.1M</td>
              </tr>
              <tr>
                <td>{t('county.topBuyerCities')}</td>
                <td>Thousand Oaks, Simi Valley, Camarillo, Oxnard</td>
              </tr>
              <tr>
                <td>{t('county.topSellerMarkets')}</td>
                <td>Thousand Oaks, Ventura, Ojai</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="idxtown-vibe">
          <h2>{t('county.sellingHome', { county: t('county.venturaCounty') })}</h2>
          <p>{t('ventura.sellingP1')}</p>
          <p dangerouslySetInnerHTML={{__html: t('ventura.sellingP2')}} />
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('county.accuratePricing')}</h3>
              <p>{t('ventura.pricingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.professionalMarketing')}</h3>
              <p>{t('ventura.marketingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.expertNegotiation')}</h3>
              <p>{t('ventura.negotiationDesc')}</p>
            </div>
          </div>
        </section>

        <section className="properties-section">
          <h2>{t('county.currentHomesForSale', { county: t('county.venturaCounty') })}</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              Browse all available Ventura County listings by clicking "Search Listings" below.
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>{t('county.readyToBuyOrSell', { county: t('county.venturaCounty') })}</h2>
          <p>{t('county.contactForConsultation')}</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=Ventura County Real Estate Inquiry`} className="cta-button primary">
              {t('county.emailAgent')}
            </a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">
              {t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              {t('county.searchCountyListings', { county: 'Ventura' })}
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Any amount of compensation or commission stated is made only to participants of CRMLS or an MLS that has entered into a datashare agreement with CRMLS.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default VenturaCountyPage;
